import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Instagram, Facebook, Globe, Star } from "lucide-react";
import { HOSPITAL, NAV_LINKS, waLink } from "../data/site";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-navy-950 text-slate-300">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:grid-cols-2 sm:px-6 lg:grid-cols-4 lg:px-8">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-white p-1 ring-1 ring-white/20">
              <img loading="lazy" decoding="async" src="/images/logo-mark.webp" alt="Shri Sai Chest and Allergy Hospital logo" className="h-full w-full object-contain" />
            </span>
            <span className="leading-tight">
              <span className="block font-heading text-sm font-bold text-white sm:text-base">Shri Sai Chest and Allergy Hospital</span>
              <span className="block text-[9px] font-medium uppercase tracking-[0.08em] text-teal-300 sm:text-[10px]">
                Dr Amol Shembatwad Chest and Respiratory Clinic
              </span>
            </span>
          </div>
          <p className="mt-4 text-sm italic text-teal-200">“{HOSPITAL.tagline}”</p>
          <p className="mt-3 text-sm leading-relaxed text-slate-400">
            {HOSPITAL.subName}. Specialty chest, asthma and allergy care in Nanded since {HOSPITAL.established}.
          </p>
        </div>

        <div>
          <h3 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-white">Quick Links</h3>
          <ul className="mt-4 grid grid-cols-1 gap-2 text-sm">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-slate-400 transition-colors hover:text-teal-300"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-white">Reach Us</h3>
          <ul className="mt-4 space-y-3 text-sm text-slate-400">
            <li className="flex gap-2.5">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-teal-400" />
              <span>{HOSPITAL.footerAddress}</span>
            </li>
            <li>
              <a data-testid="footer-call-link" href={`tel:${HOSPITAL.phone}`} className="flex gap-2.5 transition-colors hover:text-teal-300">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-teal-400" /> {HOSPITAL.phone} (Direct Call)
              </a>
            </li>
            <li>
              <a data-testid="footer-whatsapp-link" href={waLink()} target="_blank" rel="noopener noreferrer" className="flex gap-2.5 transition-colors hover:text-teal-300">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-teal-400" /> {HOSPITAL.whatsappDisplay} (WhatsApp)
              </a>
            </li>
            <li className="flex gap-2.5">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-teal-400" />
              <span>
                OPD — {HOSPITAL.opd.days}: {HOSPITAL.opd.morning} &amp; {HOSPITAL.opd.evening}
                <span className="mt-1 block font-semibold text-rose-400">24×7 Emergency Respiratory Care</span>
              </span>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-white">Follow Us</h3>
          <div className="mt-4 flex gap-3">
            <a data-testid="footer-social-instagram" href={HOSPITAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/10 transition-colors hover:bg-brand hover:text-white">
              <Instagram className="h-4 w-4" />
            </a>
            <a data-testid="footer-social-facebook" href={HOSPITAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/10 transition-colors hover:bg-brand hover:text-white">
              <Facebook className="h-4 w-4" />
            </a>
            <a data-testid="footer-social-justdial" href={HOSPITAL.justdialLink} target="_blank" rel="noopener noreferrer" aria-label="Justdial"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/10 transition-colors hover:bg-brand hover:text-white">
              <Globe className="h-4 w-4" />
            </a>
            <a data-testid="footer-social-google" href={HOSPITAL.googleLink} target="_blank" rel="noopener noreferrer" aria-label="Google reviews"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/10 transition-colors hover:bg-brand hover:text-white">
              <Star className="h-4 w-4" />
            </a>
          </div>
          <Link
            to="/book-appointment"
            data-testid="footer-book-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-light"
          >
            Book Appointment
          </Link>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} {HOSPITAL.name}, Nanded. All rights reserved.
      </div>
    </footer>
  );
}
